/**
 * Formatadores monetarios pt-PT.
 * Todos os valores em EUR, com fallback seguro para inputs nulos.
 */
const eurFormatter = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const eurCompactFormatter = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Format a number as EUR currency (pt-PT locale).
 * @param {number | null | undefined} value
 * @param {{ compact?: boolean }} [options]
 * @returns {string}
 */
export function formatEUR(value, { compact = false } = {}) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return compact ? eurCompactFormatter.format(value) : eurFormatter.format(value);
}

/**
 * Parse a pt-PT formatted number string into a JS number.
 *
 * pt-PT convention is "." for thousands, "," for decimals (ex: "1.234,56").
 * But `NumericInput` (inputMode="decimal") also lets a bare "." through —
 * many mobile keyboards send "." for the decimal key regardless of locale —
 * so a lone "." can mean either separator. Disambiguate by digit count:
 * thousands groups are always exactly 3 digits, so 1/2/4+ digits after a
 * single "." can only be a decimal point ("45.5" -> 45.5, "0.05" -> 0.05,
 * "0.12345678" -> crypto-precision decimal). Exactly 3 digits stays
 * ambiguous ("45.000") and defaults to the thousands reading, matching
 * existing whole-euro-amount behaviour. Multiple "." can only be thousands
 * separators (a real decimal number has at most one separator).
 * @param {string} input
 * @returns {number | null}
 */
export function parsePtNumber(input) {
  if (typeof input !== 'string') return null;
  let cleaned = input.replace(/\s/g, '');

  if (cleaned.includes(',') && cleaned.includes('.')) {
    // Both present — whichever comes last is the decimal separator.
    if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (cleaned.includes(',')) {
    cleaned = cleaned.replace(',', '.');
  } else if (cleaned.includes('.')) {
    const parts = cleaned.split('.');
    const isThousandsGroup = parts.length === 2 && parts[1].length === 3;
    if (parts.length > 2 || isThousandsGroup) {
      cleaned = cleaned.replace(/\./g, '');
    }
    // else: single "." with 1, 2, or 4+ trailing digits — it's the decimal point, leave as-is.
  }

  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/**
 * Converte um valor de montante (string pt-PT, number, null/undefined) num
 * number finito com fallback 0. Mais seguro que `Number(x || 0)` directo:
 * - "1.234,56" -> 1234.56 (stripping de separadores de milhares)
 * - null/undefined/"" -> 0
 * - "abc" -> 0
 * Usado para normalizar `item.amount` vindo da API antes de aritmética/UI.
 * @param {string | number | null | undefined} value
 * @returns {number}
 */
export function parseMoney(value) {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  return parsePtNumber(value) ?? 0;
}
